import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Utensils, Leaf, ArrowRight, Info } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
export function ReportConcernPage() {
  return <div className="min-h-screen flex flex-col w-full bg-gov-light" data-id="element-383">
      <Header data-id="element-384" />

      <main className="flex-grow" data-id="element-385">
        {/* Page Header */}
        <div className="bg-gov-navy text-white py-12 md:py-16" data-id="element-386">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-387">
            <div className="flex items-center space-x-3 mb-4" data-id="element-388">
              <div className="bg-gov-amber/20 p-2 rounded-lg" data-id="element-389">
                <AlertTriangle className="w-6 h-6 text-gov-amber" data-id="element-390" />
              </div>
              <span className="text-gov-amber font-semibold tracking-wider uppercase text-sm" data-id="element-391">
                Public Health Services
              </span>
            </div>
            <h1 className="text-3xl md:text-5xl font-black mb-4" data-id="element-392">
              Report a Concern
            </h1>
            <p className="text-lg md:text-xl text-gray-300 max-w-3xl leading-relaxed" data-id="element-393">
              Help us keep Franklin County safe. Select the type of concern
              below to ensure your report is routed to the correct department
              for immediate review.
            </p>
          </div>
        </div>

        {/* Triage Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-id="element-394">
          {/* Emergency Notice */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-12 rounded-r-lg flex items-start shadow-sm max-w-4xl mx-auto" data-id="element-395">
            <Info className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" data-id="element-396" />
            <div data-id="element-397">
              <h3 className="text-blue-800 font-bold mb-1" data-id="element-398">
                Is this a medical emergency?
              </h3>
              <p className="text-blue-700 text-sm" data-id="element-399">
                If you or someone else is experiencing a life-threatening
                medical emergency, please call{' '}
                <strong className="font-black" data-id="element-400">911</strong> immediately. Do not
                use this form for emergencies.
              </p>
            </div>
          </div>

          {/* Selection Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-401">
            {/* Card 1: Food Safety */}
            <motion.a href="#report-food" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: 0.1
          }} className="group relative bg-white rounded-2xl p-8 md:p-10 shadow-sm border border-gov-border hover:shadow-xl hover:border-gov-amber/50 transition-all duration-300 flex flex-col h-full" data-id="element-402">
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gov-navy rounded-t-2xl group-hover:bg-gov-amber transition-colors duration-300" data-id="element-403"></div>

              <div className="w-16 h-16 bg-gov-navy/5 text-gov-navy rounded-xl flex items-center justify-center mb-6 group-hover:bg-gov-navy group-hover:text-white transition-colors duration-300" data-id="element-404">
                <Utensils className="w-8 h-8" data-id="element-405" />
              </div>

              <h2 className="text-2xl font-bold text-gov-navy mb-4 group-hover:text-gov-amber transition-colors" data-id="element-406">
                Foodborne Illness or Restaurant Complaint
              </h2>

              <p className="text-gov-slate mb-8 flex-grow leading-relaxed text-lg" data-id="element-407">
                Report a suspected food-related illness, unsanitary conditions,
                or improper food handling at a restaurant, grocery store, or
                food truck in Franklin County.
              </p>

              <div className="flex items-center text-gov-navy font-bold mt-auto group-hover:text-gov-amber transition-colors" data-id="element-408">
                Start Food Safety Report{' '}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-2 transition-transform" data-id="element-409" />
              </div>
            </motion.a>

            {/* Card 2: Environmental Health */}
            <motion.a href="#report-environmental" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: 0.2
          }} className="group relative bg-white rounded-2xl p-8 md:p-10 shadow-sm border border-gov-border hover:shadow-xl hover:border-gov-amber/50 transition-all duration-300 flex flex-col h-full" data-id="element-410">
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gov-navy rounded-t-2xl group-hover:bg-gov-amber transition-colors duration-300" data-id="element-411"></div>

              <div className="w-16 h-16 bg-gov-navy/5 text-gov-navy rounded-xl flex items-center justify-center mb-6 group-hover:bg-gov-navy group-hover:text-white transition-colors duration-300" data-id="element-412">
                <Leaf className="w-8 h-8" data-id="element-413" />
              </div>

              <h2 className="text-2xl font-bold text-gov-navy mb-4 group-hover:text-gov-amber transition-colors" data-id="element-414">
                Environmental Health Concern
              </h2>

              <p className="text-gov-slate mb-8 flex-grow leading-relaxed text-lg" data-id="element-415">
                Report concerns regarding public pools, spas, lodging
                facilities, water quality, mold, bed bugs, sewage leaks, or
                other environmental health hazards.
              </p>

              <div className="flex items-center text-gov-navy font-bold mt-auto group-hover:text-gov-amber transition-colors" data-id="element-416">
                Start Environmental Report{' '}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-2 transition-transform" data-id="element-417" />
              </div>
            </motion.a>
          </div>

          {/* Alternative Contact */}
          <div className="mt-16 text-center max-w-2xl mx-auto" data-id="element-418">
            <p className="text-gov-slate" data-id="element-419">
              Not sure which option to choose? You can also report concerns by
              calling our main office at{' '}
              <strong className="text-gov-navy font-semibold" data-id="element-420">
                (555) 123-4567
              </strong>{' '}
              during regular business hours.
            </p>
          </div>
        </div>
      </main>

      <Footer data-id="element-421" />
    </div>;
}